'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useEffect, useMemo, useState } from 'react'
import { Card } from '@cocoonspace/ui/components/card'
import {
	Carousel,
	type CarouselApi,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from '@cocoonspace/ui/components/carousel'
import { useSearchParams } from 'next/navigation'

import { Popup } from '~/components/maplibre'
import {
	type QueryFilters,
	useSearchFilters,
} from '../filters/use-search-filters.hook'
import { SpaceCard } from '../space-card'
import type { SelectedSpaces } from './types'

export const SpacesPopup = ({
	spaces,
	selectedSpaces,
	onClose,
}: {
	spaces: Space[]
	selectedSpaces: SelectedSpaces
	onClose: VoidFunction
}) => {
	const { filters } = useSearchFilters()

	const spacesNarowed = useMemo(
		() => spaces.filter((space) => selectedSpaces.spaceIds.includes(space.id)),
		[spaces, selectedSpaces.spaceIds],
	)

	return (
		<Popup
			anchor='bottom'
			latitude={selectedSpaces.coordinates.lat}
			longitude={selectedSpaces.coordinates.lng}
			onClose={onClose}
		>
			<Card className='overflow-hidden'>
				<SpacesCarousel
					filters={filters}
					spaces={spacesNarowed}
				/>
			</Card>
		</Popup>
	)
}

export const SpacesCarousel = ({
	spaces,
	filters,
}: {
	spaces: Space[]
	filters?: Omit<QueryFilters, 'isMapVisible' | 'sortBy' | 'sortOrder'>
}) => {
	const searchParams = useSearchParams()
	const [api, setApi] = useState<CarouselApi>()

	const [currentIndex, setCurrentIndex] = useState(1)

	useEffect(() => {
		if (!api) {
			return
		}
		const updateIndex = () => setCurrentIndex(api.selectedScrollSnap() + 1)

		updateIndex()

		api.on('select', updateIndex)

		return () => {
			api.off('select', updateIndex)
		}
	}, [api])

	const queryParams = useMemo(() => {
		if (!filters) {
			return undefined
		}
		const params = new URLSearchParams(searchParams.toString())
		params.delete('day')
		params.delete('end')
		params.delete('start')
		params.delete('step')

		if (filters.day) {
			params.set('day', filters.day.join(','))
			params.set(
				'step',
				filters.start && filters.end ? 'timeSelect' : 'multiDaysSelect',
			)
		}

		if (filters.end) {
			params.set('end', filters.end.replace(':', '-'))
		}

		if (filters.start) {
			params.set('start', filters.start.replace(':', '-'))
		}

		return params.toString()
	}, [filters, searchParams])

	return (
		<Carousel
			className='divide-y'
			setApi={setApi}
		>
			<CarouselContent>
				{spaces.map((space) => (
					<CarouselItem key={space.id}>
						<SpaceCard
							className='flex w-[300px] hover:ring-0'
							space={space}
							href={`${`/${space.zone}/${space.slug}`}${queryParams ? `?${queryParams}` : ''}`}
						/>
					</CarouselItem>
				))}
			</CarouselContent>

			{spaces.length > 1 && (
				<div className='flex flex-row items-center px-2 py-1 text-sm'>
					<CarouselPrevious
						className='relative inset-auto mr-auto translate-y-0'
						size='sm'
					/>

					{`${currentIndex}/${spaces.length}`}

					<CarouselNext
						className='relative inset-auto ml-auto translate-y-0'
						size='sm'
					/>
				</div>
			)}
		</Carousel>
	)
}
